import { useEffect, useRef } from "react";

import { useActiveTask, useLogger } from "@/sdk";

import { getBusinessToday } from "../data/businessDay";
import { useMorningPaperStore } from "./morningPaper.store";

// To re-show the paper during local dev (after dismissing it), clear the
// persisted dismissal and reload:
//   localStorage.removeItem("morning-paper-store"); location.reload();
//
// The flag is passed in (not read via useFeatures) so Home.tsx can resolve
// it from LaunchDarkly inside the existing pre-workspace loading gate. Without
// this, useFeatures would synchronously return the false default and the
// workspace would render briefly before swapping to the morning paper once LD
// responded — a visible first-paint flash.
export function useShouldShowMorningPaper(isMorningPaperEnabled: boolean): boolean {
    const lastDismissedDate = useMorningPaperStore((state) => state.lastDismissedDate);
    const activeTask = useActiveTask();
    const logger = useLogger({ module: "MorningPaperVisibility" });
    const hasAlreadyLoggedSkipReason = useRef(false);

    const today = getBusinessToday();
    const dismissedToday = lastDismissedDate === today;

    useEffect(() => {
        if (hasAlreadyLoggedSkipReason.current) return;
        if (!isMorningPaperEnabled || !dismissedToday) return;
        hasAlreadyLoggedSkipReason.current = true;
        logger.info({ lastDismissedDate, today }, "Morning paper: skipping render — already dismissed for today");
    }, [dismissedToday, isMorningPaperEnabled, lastDismissedDate, logger, today]);

    if (!isMorningPaperEnabled) return false;
    if (activeTask) {
        if (!hasAlreadyLoggedSkipReason.current) {
            hasAlreadyLoggedSkipReason.current = true;
            logger.info({ taskId: activeTask.id }, "Morning paper: skipping — active task present");
        }
        return false;
    }
    return !dismissedToday;
}
